class MainMenu {
    /**
     * @param root {Element|HTMLElement}
     */
    constructor(root) {
        this.root = root

        this.isTouchSupported = 'ontouchstart' in window || (window.DocumentTouch && document instanceof window.DocumentTouch) || navigator.maxTouchPoints > 0 || window.navigator.msMaxTouchPoints > 0
        this.isDesktop = window.matchMedia('(min-width: 1024px)')
        this.activeMenuTimeout = null

        this.initializeElements()
        this.initializeEvents()
    }

    initializeElements() {
        this.menuItems = this.root.querySelectorAll('[data-component-part="menuItem"]')

        window.addEventListener('resize', this.handleActiveMenu.bind(this))
        window.addEventListener('resize', this.calculateMenuBackgroundPosition.bind(this))
        this.handleActiveMenu()
        window.addEventListener('load', () => {
            // Do initial calculation after all styles and images are loaded
            this.calculateMenuBackgroundPosition()
        })
        this.root.addEventListener('headerAnimated', () => {
            // Recalculate after the header has been animated, as the menu item positions change by that
            this.calculateMenuBackgroundPosition()
        })
    }

    initializeEvents() {
        this.eventTarget = new EventTarget()

        this.menuItems.forEach(item => {
            if (this.isTouchSupported) {
                item.addEventListener('touchstart', ev => {
                    if (this.openSubMenu(item)) {
                        // Prevent the default link behavior, when the submenu is opened
                        ev.preventDefault()
                        this.dispatchEvent(new Event('subMenuOpened'))
                    }
                })

                this.root.addEventListener('touchstart', ev => {
                    ev.stopPropagation()
                })

                document.body.addEventListener('touchstart', () => {
                    this.menuItems.forEach(item => {
                        this.closeSubMenu(item)
                    })
                    this.dispatchEvent(new Event('subMenuClosed'))
                })
            }

            item.addEventListener('mouseenter', () => {
                this.openSubMenu(item)
                window.clearTimeout(this.activeMenuTimeout)
                this.dispatchEvent(new Event('subMenuOpened'))
            })

            item.addEventListener('mouseleave', () => {
                if (!item.classList.contains('is-active') || this.isDesktop.matches) {
                    this.closeSubMenu(item)
                    this.activeMenuTimeout = window.setTimeout(this.handleActiveMenu.bind(this), 300)
                    this.dispatchEvent(new Event('subMenuClosed'))
                }
            })
        })
    }

    calculateMenuBackgroundPosition() {
        if (this.isDesktop.matches) {
            this.menuItems.forEach(menuItem => {
                const subMenu = menuItem.querySelector('[data-component-part="subMenu"]')
                if (subMenu) {
                    const clientRect = subMenu.getBoundingClientRect()
                    menuItem.style.setProperty('--menu-bg-x-offset', clientRect.x + 'px')
                    menuItem.style.setProperty('--menu-bg-y-offset', clientRect.y + 'px')
                }
            })
        }
    }

    handleActiveMenu() {
        if (!this.isDesktop.matches) {
            this.menuItems.forEach(item => {
                if (item.classList.contains('is-active')) {
                    item.classList.add('is-open')
                }
            })
        } else {
            this.menuItems.forEach(item => {
                if (item.classList.contains('is-active')) {
                    item.classList.remove('is-open')
                }
            })
        }
    }

    /**
     * @param menuItem {Element|HTMLElement}
     */
    openSubMenu(menuItem) {
        if (menuItem.classList.contains('is-open')) {
            // Return false to keep the default link behaviour
            return false
        }

        this.menuItems.forEach(item => {
            this.closeSubMenu(item)
        })
        menuItem.classList.add('is-open')

        // Return true to prevent opening the link and open the sub menu instead
        return true
    }

    /**
     * @param menuItem {Element|HTMLElement}
     */
    closeSubMenu(menuItem) {
        menuItem.classList.remove('is-open')
    }

    addEventListener(type, listener, options = false) {
        return this.root.addEventListener(type, listener, options)
    }

    removeEventListener(type, listener, options = false) {
        return this.root.removeEventListener(type, listener, options)
    }

    dispatchEvent(event) {
        return this.root.dispatchEvent(event)
    }
}

export { MainMenu }
